import React from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import {
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';

export type OmdSupplierOption = { slug: string; name: string; configured: boolean };

function RequiredMark() {
  return <span className="text-red-500"> *</span>;
}

type OmdAddConnectionModalProps = {
  open: boolean;
  suppliers: OmdSupplierOption[];
  editSlug: string | null;
  onClose: () => void;
  onSaved: (result: { testedOk: boolean; testError?: string }) => void;
};

export function OmdAddConnectionModal({
  open,
  suppliers,
  editSlug,
  onClose,
  onSaved,
}: OmdAddConnectionModalProps) {
  const addable = React.useMemo(
    () => suppliers.filter((s) => !s.configured || s.slug === editSlug),
    [suppliers, editSlug],
  );
  const [slug, setSlug] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [hasStoredPassword, setHasStoredPassword] = React.useState(false);
  const [customerId, setCustomerId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const userRef = React.useRef<HTMLInputElement>(null);

  const selected = addable.find((s) => s.slug === slug) ?? null;
  const isEdit = Boolean(editSlug);

  const resetForm = () => {
    setUsername('');
    setPassword('');
    setPasswordVisible(false);
    setHasStoredPassword(false);
    setCustomerId('');
    setLoading(false);
    setSaving(false);
    setError(null);
  };

  React.useEffect(() => {
    if (!open) return;
    setSlug(editSlug || addable[0]?.slug || '');
    resetForm();
    // Only seed when the dialog opens or the target supplier changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addable is derived from that
  }, [open, editSlug]);

  React.useEffect(() => {
    if (!open || !slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetch(`/api/settings/omd/${encodeURIComponent(slug)}`, { credentials: 'include' })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled) return;
        if (!ok) throw new Error(d?.error || 'Zugangsdaten konnten nicht geladen werden.');
        setUsername(String(d.username ?? ''));
        setCustomerId(String(d.customerId ?? ''));
        setHasStoredPassword(Boolean(d.configured));
        setPassword('');
        setPasswordVisible(false);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, slug]);

  React.useEffect(() => {
    if (!open || loading) return;
    const t = window.setTimeout(() => userRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open, loading]);

  useEscapeClose(onClose, open && !saving);

  const saveAndTest = async () => {
    if (!slug) {
      setError('Bitte einen Großhändler wählen.');
      return;
    }
    if (!username.trim()) {
      setError('Bitte den Benutzernamen eintragen.');
      return;
    }
    if (!password.trim() && !hasStoredPassword) {
      setError('Bitte das Passwort eintragen.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const testRes = await fetch(`/api/settings/omd/${encodeURIComponent(slug)}/test`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const td = await testRes.json().catch(() => ({}));
      if (!testRes.ok) {
        setError(td?.error || 'Prüfung fehlgeschlagen.');
        return;
      }
      const saveRes = await fetch(`/api/settings/omd/${encodeURIComponent(slug)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, customerId }),
      });
      const sd = await saveRes.json().catch(() => ({}));
      if (!saveRes.ok) throw new Error(sd?.error || 'Speichern fehlgeschlagen.');
      onSaved({ testedOk: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      closeOnOverlayClick={false}
      disableClose={saving}
      overlayClassName="z-50"
      panelClassName="md:max-w-lg md:h-auto"
      ariaLabelledBy="omd-add-connection-title"
    >
      <div className="flex flex-col flex-1 min-h-0 h-full">
        <div className="px-5 py-3 border-b border-app-border flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 id="omd-add-connection-title" className="text-sm font-semibold text-app-text">
              {isEdit ? 'Katalog-Zugang bearbeiten' : 'Katalog hinzufügen'}
            </h2>
            <p className="text-xs text-app-text-subtle mt-0.5">Open Master Data für Live-Produktdaten im Katalog.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text disabled:opacity-40"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error ? (
          <div className="mx-5 mt-3 p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200 shrink-0">
            {error}
          </div>
        ) : null}

        <div className="p-5 space-y-3 overflow-y-auto">
          {loading ? <InlineLoadingState label="Zugangsdaten werden geladen …" className="justify-start" /> : null}
          {!loading && !isEdit && addable.length === 0 ? (
            <p className="text-sm text-app-text-subtle">
              Alle verfügbaren Großhändler haben bereits einen Zugang.
            </p>
          ) : null}
          {!loading && (isEdit || addable.length > 0) ? (
            <>
              <div>
                <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
                  Großhändler
                  <RequiredMark />
                </label>
                {isEdit || addable.length <= 1 ? (
                  <p className="h-9 flex items-center text-sm text-app-text">
                    {selected?.name || suppliers.find((s) => s.slug === slug)?.name || '—'}
                  </p>
                ) : (
                  <AppSelectDropdown
                    value={slug}
                    onChange={setSlug}
                    options={addable.map((s) => ({ value: s.slug, label: s.name }))}
                    className="w-full"
                    triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                  />
                )}
              </div>
              <div>
                <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
                  Benutzername
                  <RequiredMark />
                </label>
                <input
                  ref={userRef}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={SETTINGS_INPUT_CLASS}
                  autoComplete="username"
                />
              </div>
              <div>
                <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
                  Passwort
                  {hasStoredPassword ? null : <RequiredMark />}
                </label>
                <div className="relative">
                  <input
                    type={passwordVisible ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${SETTINGS_INPUT_CLASS} pr-10`}
                    autoComplete="current-password"
                    placeholder={hasStoredPassword ? 'Unverändert lassen' : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-app-text-muted hover:bg-app-hover"
                    aria-label={passwordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  >
                    {passwordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>Kundennummer</label>
                <input
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  className={SETTINGS_INPUT_CLASS}
                />
              </div>
              <button
                type="button"
                className={SETTINGS_BTN_PRIMARY}
                disabled={saving || !slug}
                onClick={() => void saveAndTest()}
              >
                {saving ? <LoadingSpinner size="sm" /> : null}
                Speichern und prüfen
              </button>
            </>
          ) : null}
        </div>
      </div>
    </AppModal>
  );
}
